import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';

import { AutomationModule } from '@frontend/sports/automation/core-feature';
import { CurrencyPipe } from '@frontend/sports/common/core/feature/money';
import { EllipsisComponent } from '@frontend/sports/common/ui/ellipsis';
import { DsButton } from '@frontend/ui/button';
import { DsButtonIcon } from '@frontend/ui/button-icon';
import { DsCheckbox } from '@frontend/ui/checkbox';
import { ObserveSizeDirective } from '@frontend/vanilla/core';
import { IconCustomComponent } from '@frontend/vanilla/shared/icons';
import { EffectsModule } from '@ngrx/effects';
import { NgScrollbarModule } from 'ngx-scrollbar';
import { ArcModule } from 'packages/sports/web/app/src/arc/arc.module';
import { SGPBadgeComponent } from 'packages/sports/web/app/src/betbuilder/components/badge/sgp-badge/sgp-badge.component';
import { SGPPlusBadgeComponent } from 'packages/sports/web/app/src/betbuilder/components/badge/sgp-plus-badge/sgp-plus-badge.component';
import { ShowInEpcotDirective } from 'packages/sports/web/app/src/common/epcot-config.directive';
import { EpcotNotificationComponent } from 'packages/sports/web/app/src/common/epcotNotifications/notification.component';
import { NumpadModule } from 'packages/sports/web/app/src/numpad/numpad.module';
import { ShareMyBetModule } from 'packages/sports/web/app/src/share-my-bet/share-my-bet.module';
import { TimerModule } from 'packages/sports/web/app/src/timers/timer.module';

import BetslipBaseModule from '../../base/betslip-base.module';
import BetslipAlertsModule from '../alerts/alerts.module';
import { BetslipDigitalCommonModule } from '../common/digital-module';
import NumpadContainerModule from '../numpad/numpad-container.module';
import BetslipDigitalPicksModule from '../picks/betslip-digital-picks.module';
import PickOddsModule from '../picks/pick-odds.module';
import { QuickDepositModule } from '../quick-deposit/quick-deposit.module';
import BetslipDigitalResultModule from '../result/betslip-digital-result.module';
import { DigitalRewardTokensModule } from '../reward-tokens/digital-reward-tokens.module';
import BetslipDigitalSummaryModule from '../summary/betslip-digital-summary.module';
import BetslipDigitalTaxationModule from '../taxation/betslip-digital-taxation.module';
import BetslipDigitalTrackingModule from '../tracking/betslip-digital-tracking.module';
import BetslipDigitalValidationModule from '../validation/betslip-digital-validation.module';
import WalletTypeContainerModule from '../wallet-type/wallet-type-container.module';
import { BetslipQuickBetStateStorageService } from './betslip-quick-bet-state-storage.service';
import { QuickBetBetBuilderNotificationComponent } from './components/pick/quick-bet-bet-builder-notification.component';
import { QuickBetBuilderLegsComponent } from './components/pick/quick-bet-builder-legs.component';
import { QuickBetPickErrorComponent } from './components/pick/quick-bet-pick-error.component';
import QuickBetPickListComponent from './components/pick/quick-bet-pick-list.component';
import { QuickBetPickNotificationComponent } from './components/pick/quick-bet-pick-notification.component';
import { QuickBetPicksHandlerContainerComponent } from './components/pick/quick-bet-picks-handler-container.component';
import QuickBetSinglePickComponent from './components/pick/quick-bet-single-pick.component';
import { QuickBetAccaBoostLadderComponent } from './components/quick-bet-acca-boost-ladder.component';
import { QuickBetAccaBoostNotificationComponent } from './components/quick-bet-acca-boost-notification.component';
import { QuickBetActionButtonComponent } from './components/quick-bet-action-button.component';
import { QuickBetContainerComponent } from './components/quick-bet-container.component';
import { QuickBetFooterComponent } from './components/quick-bet-footer.component';
import { QuickBetHeaderComponent } from './components/quick-bet-header.component';
import { QuickBetHelpComponent } from './components/quick-bet-help.component';
import { QuickBetSkeletonComponent } from './components/quick-bet-skeleton.component';
import { QuickBetComponent } from './components/quick-bet.component';
import { QuickBetConfirmationContainerComponent } from './components/result/quick-bet-confirmation-container.component';
import { QuickBetPlaceBetErrorModalComponent } from './components/result/quick-bet-place-bet-error-modal.component';
import { QuickBetPlaceBetWarningComponent } from './components/result/quick-bet-place-bet-warning.component';
import { QuickBetTechnicalErrorModalComponent } from './components/result/quick-bet-technical-error-modal.component';
import { QuickBetStakeComponent } from './components/stake/quick-bet-stake.component';
import { QuickBetSummaryComponent } from './components/summary/quick-bet-summary.component';
import { QuickBetTaxationLiabilityComponent } from './components/taxation/quick-bet-taxation-liability.component';
import { QuickBetTaxationZaComponent } from './components/taxation/quick-bet-taxation-za.component';
import { QuickBetTotalReturnsComponent } from './components/total-returns/quick-bet-total-returns.component';
import { QuickBetEachWayTotalStakeComponent } from './components/total-stake/quick-bet-each-way-total-stake.component';
import { TurnoverTaxComponent } from './components/turnover-tax.component';
import { ClickOutsideOrScrollDirective } from './directives/click-outside-or-scroll.directive';
import { NavigateBetslipDirective } from './directives/navigate-betslip.directive';
import { QuickBetActionButtonEffects } from './effects/quick-bet-action-button.effects';
import { QuickBetGroupPickEffects } from './effects/quick-bet-group-pick.effects';
import QuickBetLinearEffects from './effects/quick-bet-linear.effects';
import PlaceBetEffects from './effects/quick-bet-place-bet.effects';
import { QuickBetPlaceEffects } from './effects/quick-bet-place.effects';
import { QuickBetStakeEffects } from './effects/quick-bet-stake.effects';
import { QuickBetTrackingEffects } from './effects/quick-bet-tracking.effects';
import { QuickBetUndoEffects } from './effects/quick-bet-undo.effects';
import { QuickBetEffects } from './effects/quick-bet.effects';
import { QuickBetAutoScrollingService } from './services/quick-bet-auto-scrolling.service';
import { QuickBetErrorTrackingService } from './services/quick-bet-error-tracking.service';
import { QuickBetHeaderService } from './services/quick-bet-header.service';
import { QuickBetLayoutProvider } from './services/quick-bet-layout.provider';
import { QuickBetModalDialogService } from './services/quick-bet-modal-dialog.service';
import { QuickBetPickDetailsService } from './services/quick-bet-pick-details.service';
import { QuickBetRendererService } from './services/quick-bet-renderer.service';
import { QuickBetResultSlipProcessorService } from './services/quick-bet-result-slip-processor.service';
import { QuickBetReturnsMessageService } from './services/quick-bet-returns-message.service';
import { QuickBetTrackingService } from './services/quick-bet-tracking.service';
import { QuickBetUtils } from './services/quick-bet-utils.service';
import { QuickBetService } from './services/quick-bet.service';

@NgModule({
    imports: [
        ArcModule,
        BetslipAlertsModule,
        CommonModule,
        BetslipBaseModule,
        BetslipDigitalCommonModule,
        EffectsModule.forFeature([
            QuickBetActionButtonEffects,
            QuickBetGroupPickEffects,
            QuickBetPlaceEffects,
            QuickBetStakeEffects,
            QuickBetTrackingEffects,
            QuickBetEffects,
            PlaceBetEffects,
            QuickBetUndoEffects,
            QuickBetLinearEffects,
        ]),
        BetslipDigitalPicksModule,
        NumpadModule,
        BetslipDigitalTaxationModule,
        NgScrollbarModule,
        BetslipDigitalTrackingModule,
        BetslipDigitalResultModule,
        ShareMyBetModule,
        QuickDepositModule,
        DigitalRewardTokensModule,
        BetslipDigitalSummaryModule,
        BetslipDigitalValidationModule,
        WalletTypeContainerModule,
        NumpadContainerModule,
        PickOddsModule,
        ShowInEpcotDirective,
        EpcotNotificationComponent,
        TimerModule,
        QuickBetHeaderComponent,
        AutomationModule,
        IconCustomComponent,
        DsButtonIcon,
        DsButton,
        DsCheckbox,
        QuickBetConfirmationContainerComponent,
        QuickBetAccaBoostLadderComponent,
        SGPBadgeComponent,
        SGPPlusBadgeComponent,
        QuickBetFooterComponent,
        NavigateBetslipDirective,
        QuickBetActionButtonComponent,
        ClickOutsideOrScrollDirective,
        CurrencyPipe,
        ObserveSizeDirective,
        QuickBetAccaBoostNotificationComponent,
        EllipsisComponent,
    ],
    declarations: [
        QuickBetContainerComponent,
        QuickBetSkeletonComponent,
        QuickBetComponent,
        QuickBetStakeComponent,
        QuickBetBuilderLegsComponent,
        QuickBetPickErrorComponent,
        QuickBetPickNotificationComponent,
        QuickBetPickListComponent,
        QuickBetTechnicalErrorModalComponent,
        QuickBetPlaceBetErrorModalComponent,
        QuickBetPlaceBetWarningComponent,
        QuickBetHelpComponent,
        QuickBetTotalReturnsComponent,
        QuickBetTaxationZaComponent,
        TurnoverTaxComponent,
        QuickBetTaxationLiabilityComponent,
        QuickBetBetBuilderNotificationComponent,
        QuickBetSummaryComponent,
        QuickBetSinglePickComponent,
        QuickBetPicksHandlerContainerComponent,
        QuickBetEachWayTotalStakeComponent,
    ],
    exports: [QuickBetContainerComponent],
    providers: [
        QuickBetService,
        QuickBetModalDialogService,
        QuickBetLayoutProvider,
        QuickBetUtils,
        BetslipQuickBetStateStorageService,
        QuickBetTrackingService,
        QuickBetAutoScrollingService,
        QuickBetPickDetailsService,
        QuickBetReturnsMessageService,
        QuickBetHeaderService,
        QuickBetResultSlipProcessorService,
        QuickBetErrorTrackingService,
        QuickBetRendererService,
    ],
})
export class BetslipDigitalQuickBetModule {
    constructor(quickBetLayoutProvider: QuickBetLayoutProvider) {
        quickBetLayoutProvider.init();
    }
}
